import type { FC } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { useTranslation } from '@/hooks/useTranslation';
import type { SignalsChannel, SignalsPass } from '../type/SignalsTypes';
import { channelColor, channelLabel, makeTimeTickFormatter } from '../lib/signalChannels';

interface SignalChartOverlayProps {
  channels: SignalsChannel[];
  passes: SignalsPass[];
  startTime: string;
}

export const SignalChartOverlay: FC<SignalChartOverlayProps> = ({ channels, passes, startTime }) => {
  const { t } = useTranslation();
  const formatTick = makeTimeTickFormatter(startTime);

  // The first pass already starts at the left edge of the chart, so only the
  // boundaries between passes (i > 0) are worth marking.
  const boundaries = passes.slice(1);

  return (
    <div data-testid="chart-overlay">
      <ResponsiveContainer width="100%" height={380}>
        <LineChart margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" opacity={0.4} />
          <XAxis
            type="number"
            dataKey="t"
            domain={[0, 'dataMax']}
            tickFormatter={formatTick}
            tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }}
            label={{
              value: t('signals.chart.timeAxisLabel'),
              position: 'insideBottom',
              offset: -2,
              fontSize: 10,
              fill: 'var(--muted-foreground)',
            }}
          />
          <YAxis tick={{ fontSize: 10, fill: 'var(--muted-foreground)' }} width={35} />
          <Tooltip
            labelFormatter={(label) => formatTick(label as number)}
            contentStyle={{
              background: 'var(--popover)',
              border: '1px solid var(--border)',
              borderRadius: '8px',
              fontSize: '11px',
            }}
          />
          <Legend wrapperStyle={{ fontSize: '11px' }} />
          {boundaries.map((pass) => (
            <ReferenceLine
              key={pass.passIndex}
              x={pass.startSeconds}
              stroke="var(--border)"
              strokeDasharray="4 4"
              label={{
                value: t('signals.chart.passLabel', { passIndex: pass.passIndex }),
                position: 'top',
                fontSize: 10,
                fill: 'var(--muted-foreground)',
              }}
            />
          ))}
          {channels.map((channel, idx) => (
            <Line
              key={channel.tag}
              type="monotone"
              data={channel.points.map(([t, value]) => ({ t, value }))}
              dataKey="value"
              name={channelLabel(channel.tag, t)}
              stroke={channelColor(idx)}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};
